import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import WelcomeBoard from "@/components/passenger/WelcomeBoard";
import SpotifyPanel from "@/components/passenger/SpotifyPanel";
import PromoSlideshow from "@/components/passenger/PromoSlideshow";
import { toast } from "sonner";
import { ChevronLeft, Music2, X } from "lucide-react";

/** Rear-seat tablet kiosk: welcome board + Spotify. Staff only (driver / dispatcher). */
export default function Passenger() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const isDispatcher = user?.role === "dispatcher";
  const [trip, setTrip] = useState(null);
  const [status, setStatus] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [ads, setAds] = useState(null);
  const [showMusicIdle, setShowMusicIdle] = useState(false);
  const driverId = params.get("driver") || "";

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login", { replace: true }); return; }
    if (user.role === "customer") { nav("/customer", { replace: true }); }
  }, [user, loading, nav]);

  const loadTrip = useCallback(() => {
    if (!user || user.role === "customer") return;
    api.get("/passenger/active-trip", { params: isDispatcher && driverId ? { driver_id: driverId } : {} })
      .then(r => setTrip(r.data)).catch(() => setTrip({ active: false }));
  }, [user, isDispatcher, driverId]);

  const loadStatus = useCallback(() => api.get("/spotify/status").then(r => setStatus(r.data)).catch(() => setStatus({ configured: false })), []);
  const loadAds = useCallback(() => api.get("/kiosk/slides").then(r => setAds(r.data)).catch(() => setAds({ slides: [], defaults: [] })), []);

  useEffect(() => {
    if (!user || user.role === "customer") return;
    loadTrip(); loadStatus(); loadAds();
    const id = setInterval(loadTrip, 15000);
    const adsId = setInterval(loadAds, 5 * 60 * 1000); // pick up new ads without a reload
    return () => { clearInterval(id); clearInterval(adsId); };
  }, [user, loadTrip, loadStatus, loadAds]);

  // A trip starting always takes over; returning to idle resets the music escape hatch
  useEffect(() => { if (trip?.active) setShowMusicIdle(false); }, [trip?.active]);

  useEffect(() => {
    if (isDispatcher) api.get("/drivers").then(r => setDrivers(r.data || [])).catch(() => {});
  }, [isDispatcher]);

  useEffect(() => {
    const flag = params.get("spotify");
    if (!flag) return;
    if (flag === "connected") toast.success("Spotify connected — tablets can play music now");
    else toast.error("Spotify connection was cancelled or failed");
    params.delete("spotify"); setParams(params, { replace: true });
  }, [params, setParams]);

  const connect = async () => {
    try {
      const r = await api.get("/spotify/connect", { params: { origin: window.location.origin } });
      window.location.href = r.data.auth_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start Spotify sign-in"); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect the company Spotify account from all tablets?")) return;
    await api.post("/spotify/disconnect"); loadStatus();
  };

  if (!user || user.role === "customer") return null;
  const backHref = isDispatcher ? "/dispatcher" : "/driver";

  return (
    <div data-testid="passenger-kiosk" className="min-h-screen bg-[#050505] text-[#F5F2EB] flex flex-col select-none">
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/5 text-[10px] uppercase tracking-[0.2em] text-[#52525B]">
        <Link to={backHref} data-testid="passenger-exit" className="inline-flex items-center gap-1 hover:text-[#F5F2EB]"><ChevronLeft size={12}/>Staff</Link>
        <span>Passenger screen</span>
        {isDispatcher ? (
          <select data-testid="passenger-driver-select" value={driverId}
            onChange={e => { const p = new URLSearchParams(params); if (e.target.value) p.set("driver", e.target.value); else p.delete("driver"); setParams(p, { replace: true }); }}
            className="bg-transparent border border-white/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-[#A1A1AA] focus:outline-none max-w-[220px]">
            <option value="">Preview: choose a car / driver</option>
            {drivers.map(d => <option key={d.driver_id} value={d.driver_id}>{d.name}</option>)}
          </select>
        ) : <span>{user.name}</span>}
      </div>

      {trip === null ? (
        <div className="flex-1 bg-black" data-testid="kiosk-loading"/>
      ) : !trip.active && !showMusicIdle ? (
        <div className="flex-1 relative min-h-0" data-testid="kiosk-idle">
          <div className="absolute inset-0"><PromoSlideshow data={ads}/></div>
          <button data-testid="idle-show-music" onClick={() => setShowMusicIdle(true)}
            className="absolute top-4 right-4 inline-flex items-center gap-2 border border-white/20 bg-black/40 backdrop-blur px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-[#F5F2EB] hover:border-[#D4AF37]">
            <Music2 size={14}/> Music
          </button>
        </div>
      ) : (
        <div className="flex-1 grid lg:grid-cols-[46fr_54fr] min-h-0 relative" data-testid="kiosk-split">
          {trip && !trip.active && (
            <button data-testid="idle-back-to-slideshow" onClick={() => setShowMusicIdle(false)}
              className="absolute top-4 left-4 z-10 inline-flex items-center gap-2 border border-white/20 bg-black/40 px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-[#F5F2EB]">
              <X size={12}/> Slideshow
            </button>
          )}
          <div className="min-h-[52vh] lg:min-h-0 border-b lg:border-b-0 lg:border-r border-white/10">
            <WelcomeBoard trip={trip}/>
          </div>
          <div className="min-h-0">
            <SpotifyPanel status={status} isDispatcher={isDispatcher} onConnect={connect} onDisconnect={disconnect}/>
          </div>
        </div>
      )}
    </div>
  );
}
